// Supplier catalogue rows: one per product and unit, priced per unit and per base unit (either field sets the other).
// Saved in one go through PUT /supplier-inventory/:clientId/catalogue, which works the base price out again.
import { type ProductUnit, defaultUnitId, factorOf, unitsOfProduct } from '../../components/purchasing/units';
import { parseNumber, priceText } from '../Order/orderLineRules';

export const CATALOGUE_STATUS = { Active: 1, Discontinued: 2 } as const;

export interface CatalogueLine {
  /** The saved row's id, else a key for a row added on the page. */
  key: string;
  id: string | null;
  productId: string;
  name: string;
  sku: string;
  group: string;
  units: ProductUnit[];
  productUOMId: string;
  /** The supplier's price for one unit of the row (a case of 24), as typed. */
  cost: string;
  /** The same price for one base unit (a bottle), as typed. */
  unitCost: string;
  active: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;
const valid = (value: number | null): value is number => value !== null && Number.isFinite(value) && value >= 0;

/** Product and unit: one row each on a catalogue. */
export const unitKey = (productId: string, productUOMId: string) => `${productId}|${productUOMId}`;

export const baseUnitName = (line: Pick<CatalogueLine, 'units'>): string => line.units.find((unit) => unit.isBase)?.name ?? '';

/** A row as GET /supplier-inventory/:clientId/catalogue returns it. */
export const lineOfRow = (row: any): CatalogueLine => ({
  key: String(row?.id ?? ''),
  id: row?.id ? String(row.id) : null,
  productId: String(row?.productId ?? row?.product?.id ?? ''),
  name: row?.product?.productName ?? '',
  sku: row?.product?.sku ?? '',
  group: row?.product?.productGroup?.groupName ?? '',
  units: unitsOfProduct(row?.product),
  productUOMId: String(row?.productUOMId ?? ''),
  cost: priceText(Number(row?.cost) || 0),
  unitCost: priceText(Number(row?.unitCost) || 0),
  active: Number(row?.statusId) !== CATALOGUE_STATUS.Discontinued,
});

/** A product picked in Add products, in the unit chosen there; its prices are still to be entered. */
export const lineOfPick = (product: any, productUOMId: string, takenKeys: Set<string>): CatalogueLine => {
  const units = unitsOfProduct(product);
  const base = `new:${unitKey(String(product?.id ?? ''), productUOMId)}`;
  let key = base;
  for (let n = 2; takenKeys.has(key); n++) key = `${base}#${n}`;
  return {
    key,
    id: null,
    productId: String(product?.id ?? ''),
    name: product?.productName ?? '',
    sku: product?.sku ?? '',
    group: product?.productGroup?.groupName ?? '',
    units,
    productUOMId: productUOMId || defaultUnitId(units),
    cost: '',
    unitCost: '',
    active: true,
  };
};

/** The unit price typed: the base unit price follows (24.00 a case of 24 is 1.00 a bottle). */
export const withCost = (line: CatalogueLine, cost: string): CatalogueLine => {
  const n = parseNumber(cost);
  return { ...line, cost, unitCost: valid(n) ? priceText(round2(n / factorOf(line.units, line.productUOMId))) : cost.trim() === '' ? '' : line.unitCost };
};

/** The base unit price typed: the unit price follows (1.00 a bottle is 24.00 a case of 24). */
export const withUnitCost = (line: CatalogueLine, unitCost: string): CatalogueLine => {
  const n = parseNumber(unitCost);
  return { ...line, unitCost, cost: valid(n) ? priceText(round2(n * factorOf(line.units, line.productUOMId))) : unitCost.trim() === '' ? '' : line.cost };
};

/** Another unit keeps the base unit price, and prices the new unit from it. */
export const withUnit = (line: CatalogueLine, productUOMId: string): CatalogueLine => {
  const n = parseNumber(line.unitCost);
  return { ...line, productUOMId, cost: valid(n) ? priceText(round2(n * factorOf(line.units, productUOMId))) : line.cost };
};

export interface CatalogueCheck {
  costError: string;
  unitCostError: string;
  unitError: string;
  blocking: boolean;
}

const priceError = (text: string, flagged: boolean): string => {
  const n = parseNumber(text);
  if (n === null) return flagged ? 'Enter a price' : '';
  if (Number.isNaN(n)) return 'Enter a number';
  return n < 0 ? "Can't be negative" : '';
};

/** `flagged` also reports empty prices (after a save attempt). */
export const checkLine = (line: CatalogueLine, lines: CatalogueLine[], flagged: boolean): CatalogueCheck => {
  const costError = priceError(line.cost, flagged);
  // The base unit price only needs saying when it is the field that is wrong.
  const unitCostError = costError ? '' : priceError(line.unitCost, flagged);
  const twice = lines.some((other) => other.key !== line.key && unitKey(other.productId, other.productUOMId) === unitKey(line.productId, line.productUOMId));
  const unitError = !line.productUOMId ? 'Choose a unit' : twice ? 'Already in this unit' : '';
  return { costError, unitCostError, unitError, blocking: !!(costError || unitCostError || unitError) };
};

export type CatalogueChange = { kind: 'added' | 'edited' | 'removed'; key: string };

const differs = (line: CatalogueLine, saved: CatalogueLine) =>
  line.productUOMId !== saved.productUOMId || parseNumber(line.cost) !== parseNumber(saved.cost) || line.active !== saved.active;

export const changesOf = (lines: CatalogueLine[], saved: CatalogueLine[]): CatalogueChange[] => {
  const before = new Map(saved.map((line) => [line.key, line]));
  const now = new Set(lines.map((line) => line.key));
  const out: CatalogueChange[] = [];
  lines.forEach((line) => {
    const old = before.get(line.key);
    if (!old) out.push({ kind: 'added', key: line.key });
    else if (differs(line, old)) out.push({ kind: 'edited', key: line.key });
  });
  saved.forEach((line) => {
    if (!now.has(line.key)) out.push({ kind: 'removed', key: line.key });
  });
  return out;
};

/** The save: rows added or changed, and the saved rows removed. The API works the base unit price out from `cost`. */
export const cataloguePayload = (lines: CatalogueLine[], saved: CatalogueLine[]) => {
  const before = new Map(saved.map((line) => [line.key, line]));
  const now = new Set(lines.map((line) => line.key));
  return {
    lines: lines
      .filter((line) => {
        const old = before.get(line.key);
        return !old || differs(line, old);
      })
      .map((line) => ({
        ...(line.id ? { id: line.id } : {}),
        productId: line.productId,
        productUOMId: line.productUOMId,
        cost: round2(parseNumber(line.cost) ?? 0),
        statusId: line.active ? CATALOGUE_STATUS.Active : CATALOGUE_STATUS.Discontinued,
      })),
    removedIds: saved.filter((line) => line.id && !now.has(line.key)).map((line) => line.id as string),
  };
};
